import { logger } from "@/utils/debug";
import type { ApiResponseList } from "@/types/api";
import type {
  Category,
  Country,
  DatasetLanguage,
  DatasetMetadata,
  DatasetResponse,
  DatasetResultResponse,
  Token,
  User,
} from "@/types/europafacts";
import type { Query, QueryOptions } from "@/types/europafacts/Query";
import type { DataflowData } from "@/types/stores/dataflow";

import { indexedDBService } from "../storage";

const ACCESS_TOKEN_KEY = "europafacts_access_token";
const REFRESH_TOKEN_KEY = "europafacts_refresh_token";

const DEFAULT_USER: User = {
  id: "355dfa90-c2ad-4ec2-b6b5-e3aa23695f71",
  username: "admin",
  email: "kasper@stubio.dk",
  first_name: "Kasper",
  last_name: "Stubio",
};

let tokenExpirationHandler: (() => void) | null = null;

/**
 * Sets the token expiration handler
 * @param handler - The handler to set
 */
export const setTokenExpirationHandler = (handler: () => void) => {
  tokenExpirationHandler = handler;
};

export class Europafacts {
  /**
   * Gets the access token from local storage
   * @returns The access token
   */
  private static getAccessToken(): string {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token)
      throw new Error("Authentication required - no access token found");
    return token;
  }

  /**
   * Sets the access and refresh tokens
   * @param accessToken - The access token
   * @param refreshToken - The refresh token
   */
  private static setTokens(accessToken: string, refreshToken: string): void {
    if (accessToken && refreshToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /**
   * Clears the access and refresh tokens
   */
  private static clearTokens(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    if (tokenExpirationHandler) {
      tokenExpirationHandler();
    }
  }

  /**
   * Logs in a user
   * @param username - The username
   * @param password - The password
   * @returns The login response
   */
  static async login(username: string, password: string): Promise<Token> {
    const response = await fetch("/europafacts/db-users/user/login/api/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Login failed: ${response.status} - ${errorText}`);
    }

    const token: Token = await response.json();
    this.setTokens(token.access, token.refresh);
    return token;
  }

  /**
   * Gets the current user
   * @returns The current user or null if not authenticated
   */
  static async getCurrentUser(): Promise<User | null> {
    try {
      this.getAccessToken();
      return DEFAULT_USER;
    } catch {
      return null;
    }
  }

  /**
   * Checks if the user is authenticated
   * @returns Whether the user is authenticated
   */
  static isAuthenticated(): boolean {
    try {
      this.getAccessToken();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Logs out the user
   */
  static logout(): void {
    this.clearTokens();
  }

  /**
   * Validates the tokens
   * @returns Whether the tokens are valid
   */
  static async validateTokens(): Promise<boolean> {
    return this.isAuthenticated();
  }

  /**
   * Gets the token expiration time
   * @returns The token expiration time
   */
  static getTokenExpirationTime(): number | null {
    try {
      const token = this.getAccessToken();
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp ? payload.exp * 1000 : null;
    } catch (error) {
      logger.error("Failed to parse token expiration:", error);
      return null;
    }
  }

  /**
   * Checks if the token is expired
   * @returns Whether the token is expired
   */
  static isTokenExpired(): boolean {
    const expirationTime = this.getTokenExpirationTime();
    if (!expirationTime) return false;
    return Date.now() >= expirationTime;
  }

  /**
   * Validates token and throws if expired
   * @throws Error if token is expired
   */
  private static validateToken(): void {
    if (this.isTokenExpired()) {
      this.clearTokens();
      throw new Error("Authentication failed - token has expired");
    }
  }

  /**
   * Handles API response errors
   * @param response - The fetch response
   * @param errorText - The error text from the response
   */
  private static handleApiError(response: Response, errorText: string): never {
    if (response.status === 401 || response.status === 403) {
      this.clearTokens();
      throw new Error("Authentication failed - please log in again");
    }
    throw new Error(`API request failed: ${response.status} - ${errorText}`);
  }

  /**
   * Handles network errors
   * @param error - The error that occurred
   */
  private static handleNetworkError(error: unknown): never {
    if (
      error instanceof TypeError &&
      error.message.includes("Failed to fetch")
    ) {
      throw new Error(
        "Network error - please check your connection and try again"
      );
    }
    throw error;
  }

  static async fetch<T>(
    url: string,
    options?: RequestInit
  ): Promise<{
    data: T;
    duration: number;
    response: Response;
  }> {
    this.validateToken();
    const token = this.getAccessToken();

    const input = "/europafacts" + url;
    const init: RequestInit = options ?? {};
    init.headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...options?.headers,
    };

    const startTime = performance.now();
    const response = await fetch(input, init);
    const duration = performance.now() - startTime;

    if (!response.ok) {
      const errorText = await response.text();
      this.handleApiError(response, errorText);
    }

    const data = await response.json();
    return {
      data,
      duration,
      response,
    };
  }

  /**
   * Gets datasets from a query
   * @param query - The search query
   * @param options - Query options like limit, offset, select, etc.
   * @returns The datasets response
   */
  static async getDatasetsFromQuery(
    query: Query,
    options?: QueryOptions
  ): Promise<DatasetResponse> {
    try {
      const url = `/db/db-table/list/api`;
      const queryString = query.replaceAll("+", " ");
      const where = [
        {
          table_name_english__icontains: queryString,
        },
      ];
      const body = {
        where,
        ...options,
      };
      const result = await this.fetch<DatasetResponse>(url, {
        method: "POST",
        body: JSON.stringify(body),
      });
      return result.data;
    } catch (error) {
      this.handleNetworkError(error);
    }
  }

  /**
   * Gets the metadata for a dataset
   * @param datasetId - The ID of the dataset
   * @returns The metadata for the dataset
   */
  static async getMetadataForDataset(
    datasetId: string
  ): Promise<DatasetMetadata> {
    try {
      const url = `/datasets/${datasetId}/meta/api`;
      const result = await this.fetch<DatasetMetadata>(url);
      return result.data;
    } catch (error) {
      this.handleNetworkError(error);
    }
  }

  /**
   * Gets a dataset from the API
   * @param datasetId - The ID of the dataset
   * @returns The dataset
   */
  static async getDataset(datasetId: string): Promise<DatasetResultResponse> {
    try {
      const url = `/datasets/${datasetId}/query/json?_limit=100`;
      const result = await this.fetch<DatasetResultResponse>(url);
      return result.data;
    } catch (error) {
      this.handleNetworkError(error);
    }
  }

  /**
   * Gets all data from a dataset with pagination support
   * @param dbTableId - The database table ID of the dataset
   * @param options - Optional query options (limit per page, etc.)
   * @returns All dataset rows as an array
   */
  static async getDatasetData(
    dbTableId: string,
    options?: { limit?: number }
  ): Promise<any[]> {
    try {
      const limit = options?.limit || 1000; // Default to 1000 rows per page
      let allResults: any[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const url = `/datasets/${dbTableId}/query/json?_limit=${limit}&_offset=${offset}`;
        const result = await this.fetch<DatasetResultResponse>(url);
        const response = result.data;

        if (response.results && response.results.length > 0) {
          allResults = allResults.concat(response.results);
          offset += limit;

          // Check if there are more pages
          const totalCount = response.total_count || 0;
          hasMore = allResults.length < totalCount && response.results.length === limit;
        } else {
          hasMore = false;
        }
      }

      return allResults;
    } catch (error) {
      this.handleNetworkError(error);
    }
  }

  /**
   * Saves a dataflow to IndexedDB
   * @param dataflowData - The dataflow data to save
   * @param name - The name of the dataflow
   * @param description - Optional description
   * @returns The saved dataflow
   */
  static async saveDataflow(
    dataflowData: DataflowData,
    name: string,
    description?: string,
    projectId?: string
  ) {
    if (!projectId && !dataflowData.project_id) {
      throw new Error("Project ID is required to save a dataflow");
    }

    try {
      const dataflowId = dataflowData.dataflow_id || crypto.randomUUID();
      const now = new Date().toISOString();

      const newDataflow = {
        dataflow_id: dataflowId,
        dataflow_name: name,
        dataflow_description: description || null,
        flow_definition_json:
          (dataflowData.flow_definition_json as any) || null,
        status: dataflowData.status || "draft",
        project_id: projectId || dataflowData.project_id!,
        datetime_created: now,
        datetime_updated: now,
      };

      await indexedDBService.storeDataflow(newDataflow);

      return newDataflow;
    } catch (error) {
      logger.error("Error saving dataflow:", error);
      throw error;
    }
  }

  /**
   * Updates an existing dataflow in IndexedDB
   * @param dataflowId - The ID of the dataflow to update
   * @param dataflowData - The updated dataflow data
   * @returns The updated dataflow
   */
  static async updateDataflow(dataflowId: string, dataflowData: DataflowData) {
    try {
      // Get existing dataflow
      const existingDataflow = await indexedDBService.getDataflow(dataflowId);
      if (!existingDataflow) {
        throw new Error("Dataflow not found");
      }

      const updatedDataflow = {
        ...existingDataflow,
        dataflow_name: dataflowData.dataflow_name,
        dataflow_description: dataflowData.dataflow_description || null,
        flow_definition_json:
          (dataflowData.flow_definition_json as any) || null,
        status: dataflowData.status || "draft",
        datetime_updated: new Date().toISOString(),
      };

      await indexedDBService.storeDataflow(updatedDataflow);

      return updatedDataflow;
    } catch (error) {
      logger.error("Error updating dataflow:", error);
      throw error;
    }
  }

  /**
   * Loads a dataflow from IndexedDB
   * @param dataflowId - The ID of the dataflow to load
   * @returns The dataflow data
   */
  static async loadDataflow(dataflowId: string) {
    try {
      const dataflow = await indexedDBService.getDataflow(dataflowId);

      if (!dataflow) {
        throw new Error("Dataflow not found");
      }

      return dataflow;
    } catch (error) {
      logger.error("Error loading dataflow:", error);
      throw error;
    }
  }

  /**
   * Gets the list of languages from the API
   * @returns The list of languages
   */
  static async getLanguages(): Promise<Array<DatasetLanguage>> {
    try {
      const url = `/base-tables/language/list/api/`;
      const result = await this.fetch<{ results: Array<DatasetLanguage> }>(url);
      return result.data.results;
    } catch (error) {
      this.handleNetworkError(error);
    }
  }

  /**
   * Gets the list of categories from the API
   * @returns The list of categories
   */
  static async getCategories(): Promise<Array<Category>> {
    try {
      const url = `/db/category/list/api/`;
      const result = await this.fetch<ApiResponseList<Category>>(url);
      return result.data.results;
    } catch (error) {
      this.handleNetworkError(error);
    }
  }

  /**
   * Gets the list of countries from the API
   * @returns The list of countries
   */
  static async getCountries(): Promise<Array<Country>> {
    try {
      const url = `/base-tables/country/list/api/`;
      const result = await this.fetch<ApiResponseList<Country>>(url);
      return result.data.results;
    } catch (error) {
      this.handleNetworkError(error);
    }
  }
}

export default Europafacts;
